import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mountain, ArrowRight, Check, MapPin, Home, Shield, Star, BarChart3, Users, Globe } from 'lucide-react';
import StateSelector from '../components/StateSelector';
import HimalayanPeaksFooter from '../components/HimalayanPeaksFooter';

// District-wise homestay data for Uttarakhand
const DISTRICTS = [
  { id: 'nainital', name: 'Nainital', homestays: 540, valleys: ['Nainital', 'Bhimtal', 'Mukteshwar', 'Sattal'], altitude: '1,400–2,600m', highlight: true, division: 'Kumaon' },
  { id: 'tehri', name: 'Tehri Garhwal', homestays: 390, valleys: ['Kanatal', 'Dhanaulti', 'New Tehri'], altitude: '1,500–3,600m', highlight: false, division: 'Garhwal' },
  { id: 'chamoli', name: 'Chamoli', homestays: 350, valleys: ['Valley of Flowers', 'Auli', 'Badrinath'], altitude: '1,800–7,816m', highlight: true, division: 'Garhwal' },
  { id: 'dehradun', name: 'Dehradun', homestays: 320, valleys: ['Mussoorie', 'Rishikesh', 'Chakrata'], altitude: '450–3,000m', highlight: false, division: 'Garhwal' },
  { id: 'uttarkashi', name: 'Uttarkashi', homestays: 310, valleys: ['Gangotri', 'Yamunotri', 'Har Ki Dun'], altitude: '1,100–7,000m', highlight: true, division: 'Garhwal' },
  { id: 'pauri', name: 'Pauri Garhwal', homestays: 290, valleys: ['Lansdowne', 'Khirsu', 'Chopta'], altitude: '500–3,600m', highlight: false, division: 'Garhwal' },
  { id: 'pithoragarh', name: 'Pithoragarh', homestays: 280, valleys: ['Munsiyari', 'Dharchula', 'Panchachuli'], altitude: '1,500–7,000m', highlight: true, division: 'Kumaon' },
  { id: 'almora', name: 'Almora', homestays: 260, valleys: ['Binsar', 'Jageshwar', 'Kasar Devi'], altitude: '1,600–2,400m', highlight: false, division: 'Kumaon' },
  { id: 'rudraprayag', name: 'Rudraprayag', homestays: 250, valleys: ['Kedarnath', 'Chopta', 'Tungnath'], altitude: '700–6,940m', highlight: true, division: 'Garhwal' },
  { id: 'bageshwar', name: 'Bageshwar', homestays: 220, valleys: ['Kausani', 'Pindari', 'Sunderdhunga'], altitude: '1,000–5,000m', highlight: false, division: 'Kumaon' },
  { id: 'champawat', name: 'Champawat', homestays: 140, valleys: ['Lohaghat', 'Purnagiri', 'Champawat'], altitude: '1,500–2,300m', highlight: false, division: 'Kumaon' },
  { id: 'haridwar', name: 'Haridwar', homestays: 120, valleys: ['Haridwar', 'Roorkee'], altitude: '250–400m', highlight: false, division: 'Garhwal' },
  { id: 'usn', name: 'Udham S. Nagar', homestays: 80, valleys: ['Kashipur', 'Rudrapur'], altitude: '200–500m', highlight: false, division: 'Kumaon' },
];

const TOTAL_HOMESTAYS = DISTRICTS.reduce((sum, d) => sum + d.homestays, 0);

const TIERS = [
  {
    name: 'Registered',
    tagline: 'State-Licensed Homestays',
    desc: 'Every homestay registered with the Uttarakhand Tourism Department, digitized and made discoverable. Basic listing with location, contact, and license verification.',
    icon: Home,
    color: '#8B9DAF',
    features: ['License verification badge', 'Location on district map', 'Basic contact listing', 'Searchable by valley & altitude'],
  },
  {
    name: 'Silver',
    tagline: 'GoHimalayan Quality Assured',
    desc: 'Registered homestays that have been editorially visited and quality-verified by the GoHimalayan team. Verified photos, accurate descriptions, and traveller-ready listings.',
    icon: Shield,
    color: '#A8B8C8',
    features: ['On-ground quality verification', 'Professional photography', 'AI-curated descriptions', 'Review management', 'Priority in search results'],
  },
  {
    name: 'Gold',
    tagline: 'The GoHimalayan Collective',
    desc: 'Premium partners with full storytelling pages, active marketing campaigns, direct WhatsApp booking, and dedicated account management. The finest of Dev Bhoomi hospitality.',
    icon: Star,
    color: '#D4A853',
    features: ['Luxury storytelling page', 'Direct booking integration', 'Social media marketing', 'Revenue analytics dashboard', 'Dedicated account manager', 'Featured in campaigns'],
  },
];

const VALUE_PROPS = [
  { icon: Globe, title: 'Digital Registry', desc: 'Every licensed homestay in Uttarakhand — searchable, verified, bookable. One platform for the entire state.' },
  { icon: BarChart3, title: 'Demand Intelligence', desc: 'Real-time traveller search data, Char Dham seasonal patterns, and valley-level occupancy insights for tourism planning.' },
  { icon: Users, title: 'Host Enablement', desc: 'Training, photography, and digital literacy programs — turning village hosts into confident micro-entrepreneurs.' },
  { icon: Shield, title: 'Quality Assurance', desc: 'On-ground verification that maintains standards while preserving the authentic character of Kumaoni and Garhwali homestays.' },
];

// SVG Map of Uttarakhand — simplified district boundaries
function UttarakhandMap({ activeDistrict, setActiveDistrict }) {
  const districtPaths = {
    'uttarkashi': { d: 'M60 30 L160 20 L200 50 L210 110 L170 140 L120 130 L80 90 Z', cx: 140, cy: 80 },
    'chamoli': { d: 'M200 50 L300 30 L340 70 L320 130 L270 150 L210 130 L210 110 Z', cx: 265, cy: 90 },
    'pithoragarh': { d: 'M340 70 L420 50 L440 110 L420 170 L370 180 L320 150 L320 130 Z', cx: 380, cy: 120 },
    'tehri': { d: 'M120 130 L170 140 L210 130 L220 180 L180 210 L130 200 L100 170 Z', cx: 160, cy: 170 },
    'rudraprayag': { d: 'M210 130 L270 150 L280 200 L240 220 L220 200 L220 180 Z', cx: 245, cy: 175 },
    'bageshwar': { d: 'M320 150 L370 180 L380 220 L340 240 L300 220 L280 200 L270 150 Z', cx: 325, cy: 195 },
    'pauri': { d: 'M100 170 L130 200 L180 210 L190 260 L150 280 L100 260 L70 220 Z', cx: 135, cy: 235 },
    'dehradun': { d: 'M30 120 L80 90 L120 130 L100 170 L70 220 L30 200 L20 160 Z', cx: 70, cy: 160 },
    'almora': { d: 'M280 200 L340 240 L350 280 L310 300 L260 280 L240 240 Z', cx: 298, cy: 255 },
    'nainital': { d: 'M240 240 L260 280 L310 300 L320 340 L270 360 L220 340 L200 300 Z', cx: 265, cy: 310 },
    'champawat': { d: 'M370 180 L420 170 L440 220 L420 260 L380 250 L380 220 Z', cx: 405, cy: 215 },
    'haridwar': { d: 'M30 200 L70 220 L100 260 L80 300 L40 290 L20 250 Z', cx: 58, cy: 260 },
    'usn': { d: 'M150 280 L200 300 L220 340 L190 370 L140 350 L120 310 Z', cx: 170, cy: 330 },
  };

  return (
    <svg viewBox="0 0 460 400" className="w-full max-w-2xl mx-auto" style={{ filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.3))' }}>
      <defs>
        <linearGradient id="ukMapGlow" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3B7A57" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#D4A853" stopOpacity="0.1" />
        </linearGradient>
      </defs>
      {Object.entries(districtPaths).map(([id, path]) => {
        const district = DISTRICTS.find(d => d.id === id);
        const isActive = activeDistrict === id;
        const isHighlight = district?.highlight;
        return (
          <g key={id}
            onMouseEnter={() => setActiveDistrict(id)}
            onMouseLeave={() => setActiveDistrict(null)}
            style={{ cursor: 'pointer' }}
          >
            <path
              d={path.d}
              fill={isActive ? '#3B7A57' : isHighlight ? 'rgba(59,122,87,0.35)' : 'rgba(255,255,255,0.12)'}
              stroke={isActive ? '#D4A853' : 'rgba(255,255,255,0.3)'}
              strokeWidth={isActive ? 2 : 1}
              style={{ transition: 'all 0.3s ease' }}
            />
            <text
              x={path.cx} y={path.cy}
              textAnchor="middle" dominantBaseline="middle"
              fill={isActive ? '#fff' : 'rgba(255,255,255,0.65)'}
              fontSize={isActive ? 11 : 9}
              fontFamily="Georgia, serif"
              style={{ transition: 'all 0.3s ease', pointerEvents: 'none' }}
            >
              {district?.name}
            </text>
            {isActive && (
              <text
                x={path.cx} y={path.cy + 15}
                textAnchor="middle" dominantBaseline="middle"
                fill="#D4A853"
                fontSize={8}
                fontFamily="Georgia, serif"
                style={{ pointerEvents: 'none' }}
              >
                {district?.homestays} homestays
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// Counter animation
function AnimatedNumber({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return <span>{count.toLocaleString()}+</span>;
}

export default function UttarakhandStatePage() {
  const [activeDistrict, setActiveDistrict] = useState(null);
  const activeData = DISTRICTS.find(d => d.id === activeDistrict);

  return (
    <div className="min-h-screen bg-[#141c24]" data-testid="uttarakhand-state-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#141c24]/90 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <Mountain className="w-5 h-5" />
            <span className="text-sm tracking-[0.15em] uppercase">GoHimalayan</span>
          </Link>
          <StateSelector current="uttarakhand" />
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/29292360/pexels-photo-29292360.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="" className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#141c24]/40 via-[#141c24]/40 to-[#141c24]/95" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm px-5 py-2 rounded-full mb-10">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white/70 text-[10px] tracking-[0.4em] uppercase">Uttarakhand &middot; Dev Bhoomi</span>
          </div>

          <h1 className="text-7xl sm:text-8xl md:text-9xl font-light text-white tracking-tight mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 4px 30px rgba(0,0,0,0.4)' }}>
            <AnimatedNumber target={TOTAL_HOMESTAYS} />
          </h1>

          <p className="text-white/70 text-sm tracking-[0.3em] uppercase mb-12">
            Licensed Homestays &middot; 13 Districts &middot; One Platform
          </p>

          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed max-w-3xl mx-auto mb-14"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            From the source of the Ganga to the<br />last village before Tibet
          </h2>
        </div>
      </section>

      {/* Story Section */}
      <section className="pt-24 pb-8 px-6 relative" data-testid="story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-6">Dev Bhoomi</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-white font-light italic tracking-tight leading-relaxed mb-8"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            In the land of gods,<br />
            every village is a temple of hospitality.
          </h2>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-3xl mx-auto">
            Uttarakhand's homestays sit where pilgrims pause and trekkers breathe — in terraced 
            villages where mandua roti is served on brass thalis, where grandmothers hum Garhwali 
            folk songs at dawn, and where the Panchachuli peaks glow pink before the world wakes up. 
            Every booking here is an act of preservation — keeping the young rooted, the culture alive, 
            and the mountains free from mass tourism's concrete scars.
          </p>
        </div>
      </section>

      {/* Living Map Section */}
      <section className="py-24 px-6 relative" data-testid="living-map-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-4">The Living Map</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Garhwal &amp; Kumaon. Every valley mapped.
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <UttarakhandMap activeDistrict={activeDistrict} setActiveDistrict={setActiveDistrict} />
            </div>

            <div className="space-y-6">
              {activeData ? (
                <div className="transition-all duration-300">
                  <div className="flex items-center gap-2 mb-3">
                    <p className="text-[#D4A853] text-[10px] tracking-[0.4em] uppercase">District</p>
                    <span className="text-white/20 text-[10px]">&middot;</span>
                    <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase">{activeData.division} Division</p>
                  </div>
                  <h3 className="text-4xl text-white font-light italic mb-2"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {activeData.name}
                  </h3>
                  <div className="flex items-baseline gap-3 mb-6">
                    <span className="text-5xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                      {activeData.homestays}
                    </span>
                    <span className="text-white/40 text-sm tracking-wider uppercase">Registered Homestays</span>
                  </div>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-white/40" />
                      <span className="text-white/60 text-sm">{activeData.altitude}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {activeData.valleys.map(v => (
                        <span key={v} className="text-[10px] tracking-wider uppercase px-3 py-1.5 rounded-full border border-white/15 text-white/55">
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center lg:text-left">
                  <p className="text-white/20 text-sm italic mb-6">Hover over a district to explore</p>
                  <div className="grid grid-cols-2 gap-4">
                    {DISTRICTS.filter(d => d.highlight).map(d => (
                      <div key={d.id}
                        className="border border-white/10 rounded-lg p-4 hover:border-white/25 transition-colors cursor-pointer"
                        onMouseEnter={() => setActiveDistrict(d.id)}
                      >
                        <p className="text-white/80 text-sm mb-1">{d.name}</p>
                        <p className="text-2xl text-white font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                          {d.homestays}
                        </p>
                        <p className="text-white/30 text-[10px] tracking-wider uppercase mt-1">homestays</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-white/10 pt-6 mt-8">
                <div className="flex items-center justify-between">
                  <span className="text-white/45 text-xs tracking-wider uppercase">Total across Uttarakhand</span>
                  <span className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    {TOTAL_HOMESTAYS.toLocaleString()} homestays
                  </span>
                </div>
                <div className="mt-3 h-1 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#3B7A57] to-[#D4A853] rounded-full"
                    style={{ width: '100%', animation: 'expandBar 2s ease-out' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tier Funnel */}
      <section className="py-24 px-6 border-t border-white/10" data-testid="tier-funnel">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-4">The Homestay Index</p>
            <h2 className="text-3xl sm:text-4xl text-white font-light italic tracking-tight mb-4"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Three tiers. One ecosystem.
            </h2>
            <p className="text-white/50 text-sm max-w-xl mx-auto leading-relaxed">
              Every homestay begins as Registered. Through quality verification and partnership,
              they rise through the ranks — from Silver to Gold.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map((tier, i) => (
              <div key={tier.name}
                className="relative border border-white/10 rounded-xl p-8 hover:border-white/25 transition-all group"
                style={{ background: `linear-gradient(160deg, ${tier.color}12, transparent)` }}
              >
                <div className="absolute top-6 right-6 text-white/10 text-6xl font-light"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {i + 1}
                </div>
                <tier.icon className="w-8 h-8 mb-6" style={{ color: tier.color }} />
                <h3 className="text-xl text-white font-light mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {tier.name}
                </h3>
                <p className="text-xs tracking-wider uppercase mb-4" style={{ color: tier.color }}>
                  {tier.tagline}
                </p>
                <p className="text-white/45 text-sm leading-relaxed mb-6">{tier.desc}</p>
                <ul className="space-y-2.5">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} />
                      <span className="text-white/55 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                {i < 2 && (
                  <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                    <ArrowRight className="w-5 h-5 text-white/10" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2G Section */}
      <section className="py-24 px-6 relative overflow-hidden" data-testid="b2g-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#141c24] via-[#182330] to-[#141c24]" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-4">For Tourism Departments</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-white font-light italic tracking-tight mb-6"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              We digitize your homestay registry.<br />
              <span className="text-white/55">You focus on policy.</span>
            </h2>
            <p className="text-white/45 text-sm max-w-2xl mx-auto leading-relaxed">
              GoHimalayan serves as the technology and marketing partner for state tourism departments —
              bringing every licensed homestay online, making them discoverable to millions of travellers,
              and providing real-time demand intelligence for tourism planning.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-20">
            {VALUE_PROPS.map(prop => (
              <div key={prop.title} className="border border-white/10 rounded-xl p-8 hover:border-white/20 transition-colors">
                <prop.icon className="w-6 h-6 text-[#D4A853] mb-5" />
                <h3 className="text-lg text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  {prop.title}
                </h3>
                <p className="text-white/45 text-sm leading-relaxed">{prop.desc}</p>
              </div>
            ))}
          </div>

          <div className="border border-[#D4A853]/20 rounded-xl p-10 sm:p-14 text-center"
            style={{ background: 'linear-gradient(160deg, rgba(212,168,83,0.05), transparent)' }}
          >
            <p className="text-[#D4A853] text-[10px] tracking-[0.5em] uppercase mb-6">The Partnership Model</p>
            <h3 className="text-2xl sm:text-3xl text-white font-light italic mb-6"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Built to scale. Designed to empower.
            </h3>
            <div className="max-w-2xl mx-auto space-y-4 mb-10">
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#D4A853]/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D4A853] text-xs">1</span>
                </div>
                <p className="text-white/60 text-sm">State shares its licensed homestay registry — GoHimalayan digitizes and maps every property</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#D4A853]/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D4A853] text-xs">2</span>
                </div>
                <p className="text-white/60 text-sm">Travellers discover, compare, and book directly — driving revenue into local communities</p>
              </div>
              <div className="flex items-center gap-4 text-left">
                <div className="w-8 h-8 rounded-full border border-[#D4A853]/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#D4A853] text-xs">3</span>
                </div>
                <p className="text-white/60 text-sm">State receives real-time demand intelligence, occupancy analytics, and tourism planning dashboards</p>
              </div>
            </div>
            <a href="https://wa.me/919871155963?text=Hi%20GoHimalayan%2C%20I%27d%20like%20to%20discuss%20the%20State%20Partnership%20Program%20for%20Uttarakhand."
              className="inline-flex items-center gap-3 bg-[#D4A853] text-[#0a0f14] px-8 py-3.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#e0ba6a] transition-colors"
              data-testid="b2g-cta"
              target="_blank" rel="noopener noreferrer"
            >
              Begin Conversation
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      <HimalayanPeaksFooter accentColor="#D4A853" />

      <style>{`
        @keyframes expandBar {
          from { width: 0; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}
